import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { MapPin, Mail, Award, Code2 } from "lucide-react";
import { personalInfo } from "../data/mock";

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const stats = [
    { label: "Years Experience", value: "0", icon: Award },
    { label: "Projects Completed", value: "10+", icon: Code2 },
    { label: "Leetcode problem", value: "400+", icon: Award },
  ];

  return (
    // ❌ bg-slate-900 removed
    <section id="about" className="py-20 md:py-32 bg-transparent" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            About <span className="text-teal-400">Me</span>
          </h2>
          <div className="w-20 h-1 bg-teal-400 mx-auto"></div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden flex justify-center">
              <img
                src={personalInfo.image}
                alt={personalInfo.name}
                className="
        w-full 
        max-w-sm 
        h-auto 
        relative 
        z-50 
        mx-auto 
        rounded-2xl 
        shadow-2xl 
        ring-1 ring-white/10
      "
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-500/20 to-transparent"></div>
            </div>

            {/* Decorative glow elements (stay behind image) */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-teal-500/20 rounded-full blur-xl z-10"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-cyan-500/20 rounded-full blur-xl z-10"></div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-white mb-4">
              {personalInfo.title}
            </h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              {personalInfo.bio}
            </p>

            <div className="flex flex-col gap-3 pt-4">
              <div className="flex items-center gap-3 text-slate-300">
                <MapPin className="text-teal-400" size={20} />
                <span>{personalInfo.location}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <Mail className="text-teal-400" size={20} />
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="hover:text-teal-400 transition-colors"
                >
                  {personalInfo.email}
                </a>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="flex justify-center mb-2">
                    <stat.icon className="text-teal-400" size={24} />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-slate-400">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
